import { useMemo } from 'react';
import { PessoaAvatar } from '@/components/shared/PessoaAvatar';
import type { SearchableSelectOption } from '@/components/ui/searchable-select';
import type { MultiSelectOption } from '@/components/ui/multi-select';
import { useOrganizationUsers, type UseOrganizationUsersOptions } from './useOrganizationUsers';

export interface OpcaoDePessoa extends SearchableSelectOption, MultiSelectOption {
  value: string;
  label: string;
  icon: React.ReactNode;
  keywords: string[];
}

/**
 * As pessoas da org como opções de `SearchableSelect`/`MultiSelect`, já com a foto no `icon`
 * e o e-mail nas `keywords` (a busca do cmdk acha "flavio" por "flavio@…" mesmo que o nome
 * tenha acento). Use isto em vez de mapear `useOrganizationUsers` na mão: é o que garante
 * que todo seletor de pessoa do app mostre foto.
 */
export function useOpcoesDePessoas(opts: UseOrganizationUsersOptions = {}) {
  const { data: usuarios = [], isLoading } = useOrganizationUsers(opts);
  const opcoes = useMemo<OpcaoDePessoa[]>(
    () => usuarios.map((u) => {
      const nome = u.full_name?.trim() || u.email;
      return {
        value: u.user_id,
        label: nome,
        icon: <PessoaAvatar nome={nome} avatarUrl={u.avatar_url} tamanho="sm" />,
        keywords: [u.email],
      };
    }),
    [usuarios],
  );
  const porId = useMemo(() => new Map(usuarios.map((u) => [u.user_id, u])), [usuarios]);
  return { opcoes, porId, usuarios, isLoading };
}
